import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMoments } from '../api/moments';
import Pagination from '../components/Pagination';

const EVENT_CATEGORIES = ['Theater', 'Show', 'Exhibit'];

function groupByMonth(moments) {
  const groups = {};
  moments.forEach(m => {
    const date = m.date ? new Date(m.date) : null;
    const key = date
      ? date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      : 'Unknown date';
    if (!groups[key]) groups[key] = [];
    groups[key].push(m);
  });
  return Object.entries(groups);
}

function MomentList() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [moments, setMoments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/signin'); return; }
    fetchMoments(currentPage, activeCategory);
  }, [isAuthenticated, currentPage, activeCategory]);

  const fetchMoments = async (page, category) => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      const data = await getMoments(token, null, page, null, { category });
      setMoments(data.moments || []);
      if (data.meta) {
        setCurrentPage(data.meta.current_page);
        setTotalPages(data.meta.total_pages);
        setTotalCount(data.meta.total_count);
      }
    } catch {
      setError('Failed to load moments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryClick = (cat) => {
    setActiveCategory(prev => prev === cat ? null : cat);
    setCurrentPage(1);
  };

  if (!isAuthenticated) return null;
  if (error) return <div className="error">{error}</div>;

  const groups = groupByMonth(moments);

  return (
    <div className="moment-grid-page">
      <div className="moment-grid__header">
        <h1>My Moments</h1>
        {!isLoading && (
          <span className="moment-grid__count">
            {totalCount} moment{totalCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Category filter pills */}
      <div className="moment-filters">
        {EVENT_CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => handleCategoryClick(cat)}
            className={`moment-filter-pill ${activeCategory === cat ? 'moment-filter-pill--active' : ''}`}
          >
            {cat}
          </button>
        ))}
        {activeCategory && (
          <button
            onClick={() => { setActiveCategory(null); setCurrentPage(1); }}
            className="moment-filters__reset"
          >
            Clear filter
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="loading">Loading moments...</div>
      ) : moments.length === 0 ? (
        <p className="moment-grid__empty">
          {activeCategory
            ? `No ${activeCategory} moments yet.`
            : <>No moments yet. <Link to="/events">Browse events</Link> to add some!</>}
        </p>
      ) : (
        <>
          {groups.map(([monthYear, items]) => (
            <div key={monthYear} className="moment-group">
              <h2 className="moment-group__heading">{monthYear}</h2>
              <div className="moment-grid">
                {items.map(moment => (
                  <Link
                    key={moment.id}
                    to={`/moments/${moment.id}`}
                    className="moment-grid__cell"
                  >
                    {moment.event_url_image ? (
                      <img
                        src={moment.event_url_image}
                        alt={moment.event_name}
                        className="moment-grid__img"
                      />
                    ) : (
                      <div className="moment-grid__img moment-grid__img--placeholder" />
                    )}
                    {/* Always-visible bottom caption */}
                    <div className="moment-grid__caption">
                      <p className="moment-grid__caption-title">{moment.event_name}</p>
                    </div>

                    {/* Hover overlay with description */}
                    {moment.description && (
                      <div className="moment-grid__hover">
                        <p className="moment-grid__hover-desc">
                          {moment.description.length > 80
                            ? moment.description.slice(0, 80) + '…'
                            : moment.description}
                        </p>
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
}

export default MomentList;
